import { useState } from 'react'
import { giganten } from '../../data/giganten'
import type { Gigant, GigantenTrend, Positionierung, TrendSignal } from '../../data/giganten'

// ── Styles ─────────────────────────────────────────────────────────────────
const SIGNAL_STYLE: Record<TrendSignal, { color: string; bg: string; label: string }> = {
  bullish: { color: '#22C55E', bg: 'rgba(34,197,94,0.09)',   label: '▲ Rückenwind' },
  neutral: { color: '#F59E0B', bg: 'rgba(245,158,11,0.10)', label: '→ Gemischt'   },
  bearish: { color: '#EF4444', bg: 'rgba(239,68,68,0.09)',  label: '▼ Gegenwind'  },
}

const VIEW_COLOR: Record<Positionierung['view'], string> = {
  'Übergewichten':  '#22C55E',
  'Neutral':        '#F59E0B',
  'Untergewichten': '#EF4444',
}

const AENDERUNG_LABEL: Record<Positionierung['aenderung'], string> = {
  hoch:        '↑ hochgestuft',
  runter:      '↓ heruntergestuft',
  neu:         '★ neu',
  unverändert: '— unverändert',
}

function SectionHead({ label, sub }: { label: string; sub?: string }) {
  return (
    <div className="flex items-center gap-3 mb-4">
      <span className="font-mono text-[9px] font-bold tracking-widest text-accent">{label}</span>
      {sub && <span className="font-mono text-[8px] text-t3">{sub}</span>}
      <div className="flex-1 h-px" style={{ background: 'var(--border)' }} />
    </div>
  )
}

function SignalBadge({ signal }: { signal: TrendSignal }) {
  const s = SIGNAL_STYLE[signal]
  return (
    <span className="font-mono text-[8px] font-bold px-2 py-1 rounded shrink-0"
      style={{ color: s.color, background: s.bg, border: `1px solid ${s.color}30` }}>
      {s.label}
    </span>
  )
}

// ── Gigant-Karte (expandable) ──────────────────────────────────────────────
function GigantCard({ g }: { g: Gigant }) {
  const [open, setOpen] = useState(g.id === 'blackrock')

  return (
    <div className="rounded-xl overflow-hidden" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
      <button className="w-full text-left p-4 flex flex-col gap-2.5 hover:bg-white/[0.01] transition-colors"
        aria-expanded={open} onClick={() => setOpen(o => !o)}>
        <div className="flex items-start justify-between gap-2">
          <div className="flex flex-col gap-0.5 min-w-0">
            <span className="font-bold text-[15px] text-t1">{g.name}</span>
            <span className="font-mono text-[9px] text-t3">{g.ticker} · {g.typ}</span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <SignalBadge signal={g.signal} />
            <span className="font-mono text-[9px] text-t3" aria-hidden="true">{open ? '▲' : '▼'}</span>
          </div>
        </div>
        {g.aum && (
          <div className="flex items-baseline gap-2">
            <span className="font-mono text-[22px] font-bold text-t1 tabular-nums">{g.aum}</span>
            <span className="font-mono text-[8px] text-t3">AUM{g.aumAsOf && ` · ${g.aumAsOf}`}</span>
          </div>
        )}
        <p className="font-mono text-[10px] text-accent leading-snug">{g.kennzahl}</p>
        <p className="text-[11.5px] text-t2 leading-relaxed">
          <span className="font-mono text-[8px] tracking-widest text-t3 mr-1.5">FOKUS</span>{g.fokus}
        </p>
      </button>

      {open && (
        <ul className="px-4 pb-4 pt-3 flex flex-col gap-2" style={{ borderTop: '1px solid var(--border)' }}>
          {g.bullets.map(b => (
            <li key={b} className="flex items-start gap-2 text-[12px] text-t2 leading-relaxed">
              <span className="text-accent shrink-0" aria-hidden="true">›</span>{b}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

// ── Trend-Karte ────────────────────────────────────────────────────────────
function TrendCard({ t, nr }: { t: GigantenTrend; nr: number }) {
  const s = SIGNAL_STYLE[t.signal]
  return (
    <div className="rounded-xl p-4 flex flex-col gap-3"
      style={{ background: `${s.color}05`, border: `1px solid ${s.color}22` }}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2.5 min-w-0">
          <span className="font-mono text-[11px] font-bold tabular-nums shrink-0 mt-0.5" style={{ color: s.color }}>
            {String(nr).padStart(2, '0')}
          </span>
          <h3 className="font-semibold text-[13px] text-t1 leading-snug">{t.titel}</h3>
        </div>
        <SignalBadge signal={t.signal} />
      </div>
      <p className="text-[12px] text-t2 leading-relaxed">{t.kern}</p>
      <div className="flex flex-wrap gap-1.5">
        {t.belege.map(b => (
          <span key={b} className="font-mono text-[9px] px-2 py-1 rounded"
            style={{ background: 'var(--surface-2)', color: 'var(--text-2)', border: '1px solid var(--border)' }}>
            {b}
          </span>
        ))}
      </div>
      <div className="rounded-lg p-3 mt-auto" style={{ background: 'var(--surface-2)', border: '1px solid var(--border-hi)' }}>
        <span className="font-mono text-[8px] tracking-widest text-accent block mb-1.5">WAS HEISST DAS FÜR DICH?</span>
        <p className="text-[12px] text-t1 leading-relaxed">{t.bedeutung}</p>
      </div>
    </div>
  )
}

// ── Rücknahme-Balken (Private Credit) ──────────────────────────────────────
function RuecknahmeChart() {
  const max = Math.max(...giganten.ruecknahmen.map(r => r.anfragePct))
  const limit = giganten.ruecknahmeLimitPct
  const scale = (v: number) => `${(v / max) * 100}%`

  return (
    <div className="rounded-xl p-5" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
      <div className="flex flex-col gap-3" role="list" aria-label="Rücknahmewünsche Q3 in Prozent des Fondsvermögens">
        {giganten.ruecknahmen.map(r => {
          const over = r.anfragePct > limit
          return (
            <div key={r.manager} role="listitem" className="grid grid-cols-[96px_1fr_44px] sm:grid-cols-[150px_1fr_48px] items-center gap-3">
              <div className="flex flex-col min-w-0">
                <span className="text-[12px] font-semibold text-t1 truncate">{r.manager}</span>
                <span className="font-mono text-[8px] text-t3 truncate">{r.fonds}</span>
              </div>
              <div className="relative h-3 rounded-full" style={{ background: 'var(--surface-3)' }}>
                <div className="absolute inset-y-0 left-0 rounded-full"
                  style={{ width: scale(r.anfragePct), background: over ? 'var(--neg)' : 'var(--pos)' }} />
                <div className="absolute -inset-y-1 w-px" style={{ left: scale(limit), background: 'var(--text-1)' }} aria-hidden="true" />
              </div>
              <span className="font-mono text-[12px] font-bold tabular-nums text-right"
                style={{ color: over ? 'var(--neg)' : 'var(--pos)' }}>
                {r.anfragePct.toFixed(1)}%
              </span>
            </div>
          )
        })}
      </div>
      <p className="font-mono text-[9px] text-t3 mt-4 leading-relaxed">
        Rücknahmewünsche Q3 2026 in % des Fondsvermögens. Senkrechte Linie = Limit von {limit}% pro Quartal.
        Alles rechts davon wird nicht ausgezahlt und auf spätere Quartale verschoben.
      </p>
    </div>
  )
}

// ── Page ───────────────────────────────────────────────────────────────────
export default function Giganten2026() {
  const d = giganten
  const etfGiganten = d.giganten.filter(g => g.typ === 'ETF/Index')
  const altGiganten = d.giganten.filter(g => g.typ === 'Alternative')

  return (
    <main className="max-w-[900px] mx-auto px-4 py-8">

      {/* Header */}
      <div className="flex items-center gap-3 mb-2">
        <h1 className="text-[26px] font-bold text-t1 tracking-tight">Die Giganten</h1>
        <div className="flex-1 h-px" style={{ background: 'var(--border)' }} />
        <span className="font-mono text-[9px] text-t3 tabular-nums">{d.version}</span>
      </div>
      <p className="text-[13px] text-t3 mb-6 leading-relaxed">
        Was BlackRock, Vanguard, State Street, Blackstone, Apollo und KKR gerade tun — und was Privatanleger daraus lernen können
      </p>

      {/* Summary */}
      <div className="rounded-xl p-5 mb-8" style={{ background: 'var(--accent-bg)', border: '1px solid var(--border-hi)' }}>
        <span className="font-mono text-[9px] text-accent tracking-widest font-bold block mb-2">KERNAUSSAGE Q3 2026</span>
        <p className="text-[13.5px] text-t1 leading-relaxed">{d.zusammenfassung}</p>
      </div>

      {/* ETF-Markt KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-10">
        {d.etfMarkt.map(k => (
          <div key={k.label} className="rounded-xl p-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
            <span className="font-mono text-[8px] text-t3 tracking-widest block mb-1.5 uppercase">{k.label}</span>
            <span className="font-mono text-[20px] font-bold text-t1 tabular-nums block">{k.value}</span>
            <span className="font-mono text-[8px] text-t3 leading-snug block mt-1">{k.note}</span>
          </div>
        ))}
      </div>

      {/* Trends */}
      <SectionHead label="6 TRENDS BEI DEN GIGANTEN" sub="Was die Zahlen zeigen" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-10">
        {d.trends.map((t, i) => <TrendCard key={t.id} t={t} nr={i + 1} />)}
      </div>

      {/* BII Positionierung */}
      <SectionHead label="BLACKROCK HOUSE VIEW" sub={`BlackRock Investment Institute · ${d.biiStand}`} />
      <div className="rounded-xl overflow-hidden mb-10" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                {['Anlage', 'Taktische Sicht', 'Änderung', 'Begründung'].map(h => (
                  <th key={h} scope="col" className="font-mono text-[8px] tracking-widest text-t3 font-normal px-4 py-3 uppercase">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {d.positionierung.map((p, i) => (
                <tr key={p.anlage} style={{ borderBottom: i < d.positionierung.length - 1 ? '1px solid var(--border)' : undefined }}>
                  <td className="px-4 py-3 text-[12px] font-semibold text-t1 whitespace-nowrap">{p.anlage}</td>
                  <td className="px-4 py-3">
                    <span className="font-mono text-[9px] font-bold px-2 py-1 rounded whitespace-nowrap"
                      style={{ color: VIEW_COLOR[p.view], background: `${VIEW_COLOR[p.view]}14`, border: `1px solid ${VIEW_COLOR[p.view]}30` }}>
                      {p.view}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-mono text-[9px] text-t2 whitespace-nowrap">{AENDERUNG_LABEL[p.aenderung]}</td>
                  <td className="px-4 py-3 text-[11.5px] text-t2 leading-relaxed min-w-[220px]">{p.notiz}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Index-Giganten */}
      <SectionHead label="INDEX- & ETF-GIGANTEN" sub="Liquide Produkte, Rekordzuflüsse" />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-10">
        {etfGiganten.map(g => <GigantCard key={g.id} g={g} />)}
      </div>

      {/* Alternative */}
      <SectionHead label="ALTERNATIVE-GIGANTEN" sub="Private Markets, Private Credit, KI-Infrastruktur" />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        {altGiganten.map(g => <GigantCard key={g.id} g={g} />)}
      </div>

      {/* Private Credit Rücknahmen */}
      <SectionHead label="PRIVATE CREDIT — RÜCKNAHME-STAU" sub="Semi-liquide Fonds für Privatanleger" />
      <div className="mb-10">
        <RuecknahmeChart />
      </div>

      {/* Quellen */}
      <div className="flex items-center gap-3 mb-3">
        <span className="font-mono text-[9px] font-bold tracking-widest text-t3">QUELLEN</span>
        <div className="flex-1 h-px" style={{ background: 'var(--border)' }} />
      </div>
      <ul className="flex flex-col gap-1.5 mb-6">
        {d.quellen.map(q => (
          <li key={q.url}>
            <a href={q.url} target="_blank" rel="noopener noreferrer"
              className="font-mono text-[10px] text-t3 hover:text-accent transition-colors">
              ↗ {q.title}
            </a>
          </li>
        ))}
      </ul>
      <p className="font-mono text-[9px] text-t3 leading-relaxed">
        Keine Anlageberatung. Zahlen aus Quartalsberichten und Medienberichten. Blackstone-Werte Stand Q2 (Q3-Zahlen am 22. Oktober).
      </p>
    </main>
  )
}
